import type { DataGridFilterOption, DataGridFilterOptionsLoader } from '@ibsys/kit';
import { queryClient } from '@/shared/api';
import { FILTER_OPTIONS_STALE_TIME, toFilterOptionsPage, toPersonFilterOption } from '@/shared/lib';
import { fetchConversationsClients, fetchConversationsEmployees } from './api';
import { conversationsKeys, toPersonIdsParam } from './hooks';

/**
 * Асинхронный источник опций фильтра «Клиент» для DataGrid.
 *
 * Справочник клиентов большой, поэтому список грузится страницами с серверным поиском.
 * Ссылка на функцию модульная (стабильная) — кит держит по ней кеш подписей выбранных значений,
 * и на каждый рендер грида кеш не промахивается.
 *
 * Отменой запроса владеет кеш, а не грид: запрос общий для всех, кто ждёт ту же страницу
 * справочника, и оборвать его сигналом одного фильтра нельзя — отмену получили бы и остальные
 * (в StrictMode это сам же фильтр на повторном монтировании, и список открывался с ошибкой).
 * Ответ на устаревший запрос грид отбрасывает сам — по номеру запроса, а не по сигналу.
 */
export const loadConversationClientOptions: DataGridFilterOptionsLoader = async ({ search, page, pageSize }) => {
  const offset = page * pageSize;
  const params = { limit: pageSize, offset, 'client-name': search || undefined };
  const { total, clients } = await queryClient.fetchQuery({
    queryKey: [...conversationsKeys.clients(), { ...params }],
    queryFn: ({ signal }) => fetchConversationsClients(params, signal),
    staleTime: FILTER_OPTIONS_STALE_TIME,
    retry: false,
  });
  return toFilterOptionsPage(clients, total, offset, toPersonFilterOption);
};

/**
 * Подписи выбранных клиентов, которых нет ни в одной загруженной странице:
 * отбор восстановлен из настроек таблицы, и без догрузки имён в описании отбора и в карточке
 * панели фильтров стояли бы сырые идентификаторы.
 */
export const resolveConversationClientOptions = async (
  values: Array<string | number>,
): Promise<DataGridFilterOption[]> => {
  const ids = toPersonIdsParam(values);
  if (!ids) return [];
  const { clients } = await queryClient.fetchQuery({
    queryKey: conversationsKeys.clientsByIds(ids),
    queryFn: ({ signal }) => fetchConversationsClients({ 'client-id': ids }, signal),
    staleTime: FILTER_OPTIONS_STALE_TIME,
    retry: false,
  });
  return clients.map(toPersonFilterOption);
};

/**
 * Асинхронный источник опций фильтра «Консультант» для DataGrid.
 * Устроен так же, как {@link loadConversationClientOptions}.
 */
export const loadConversationEmployeeOptions: DataGridFilterOptionsLoader = async ({ search, page, pageSize }) => {
  const offset = page * pageSize;
  const params = { limit: pageSize, offset, 'employee-name': search || undefined };
  const { total, employees } = await queryClient.fetchQuery({
    queryKey: [...conversationsKeys.employees(), { ...params }],
    queryFn: ({ signal }) => fetchConversationsEmployees(params, signal),
    staleTime: FILTER_OPTIONS_STALE_TIME,
    retry: false,
  });
  return toFilterOptionsPage(employees, total, offset, toPersonFilterOption);
};

/**
 * Подписи выбранных консультантов по их идентификаторам —
 * см. {@link resolveConversationClientOptions}.
 */
export const resolveConversationEmployeeOptions = async (
  values: Array<string | number>,
): Promise<DataGridFilterOption[]> => {
  const ids = toPersonIdsParam(values);
  if (!ids) return [];
  const { employees } = await queryClient.fetchQuery({
    queryKey: conversationsKeys.employeesByIds(ids),
    queryFn: ({ signal }) => fetchConversationsEmployees({ 'employee-id': ids }, signal),
    staleTime: FILTER_OPTIONS_STALE_TIME,
    retry: false,
  });
  return employees.map(toPersonFilterOption);
};
